import '#ui/itinerary/itinerary-print.css';

import type {
  SharedItineraryEvent,
  SharedItineraryResponse,
} from '@elara/domain/models/schemas/shared-itinerary-schemas';

import type { MapPoint } from '#ui/GoogleMap.js';

import { format, parseISO } from 'date-fns';
import React from 'react';

import { formatDateRange } from '@elara/client-core/lib/date-utils';
import { getEventTypeIcon } from '@elara/client-core/lib/itinerary-utils';
import { resolveMediaUrl } from '@elara/client-core/lib/media-url';
import { getPlaceIcon } from '@elara/client-core/lib/place-category';
import { cn, getEmojiFlag } from '@elara/client-core/lib/utils';
import { ItineraryItemEventModel } from '@elara/client-core/models/ItineraryItemEventModel';

import { primeImagesForPrint, printPage, usePrimeImagesBeforePrint } from '#lib/print.js';
import { useSharedItineraryState } from '#states/hooks.js';
import { Avatar, AvatarFallback, AvatarImage } from '#ui/avatar.js';
import { Button } from '#ui/button.js';
import { GoogleMap } from '#ui/GoogleMap.js';
import { DayHeader } from '#ui/itinerary/DayHeader.js';
import { dedupeMapPoints } from '#ui/itinerary/itinerary-map-points.js';
import { ItineraryHero } from '#ui/itinerary/ItineraryHero.js';
import { ItinerarySummarySection } from '#ui/itinerary/ItinerarySummarySection.js';
import { OverviewDestinationHeader } from '#ui/itinerary/OverviewDestinationHeader.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { MembershipBanner, MembershipCard } from './MembershipCallout.js';
import { SharedEventBlock } from './SharedEventBlock.js';

const EDGE_PADDING_CLASSES = 'px-4 md:px-8';

function toModel(event: SharedItineraryEvent): ItineraryItemEventModel {
  return ItineraryItemEventModel.from(event as Parameters<typeof ItineraryItemEventModel.from>[0]);
}

/** Map pins for stays and places, from the public venue details. Flights draw their own route maps. */
function venueMapPoints(itinerary: SharedItineraryResponse): MapPoint[] {
  const points: MapPoint[] = [];
  for (const event of itinerary.days.flatMap((day) => day.events)) {
    if (!('details' in event) || !event.details) continue;
    const { details } = event;
    if (!details.location && !details.address) continue;
    points.push({
      id: event.id,
      label: details.name,
      latitude: details.location?.lat,
      longitude: details.location?.lng,
      address: details.address,
    });
  }
  return dedupeMapPoints(points);
}

/** The overview line's leading icon, chosen as on the owner's page: airline logo, country flag, place category. */
function OverviewIcon({ event }: { event: SharedItineraryEvent }) {
  const fallback = <MaterialIcon icon={getEventTypeIcon(event.type)} size={IconSizes.sm} />;
  if (event.type === 'flight_departure' && event.metadata.segment.airline.logo_url) {
    const { airline } = event.metadata.segment;
    return (
      <Avatar className="h-5 w-5 rounded-none">
        <AvatarImage src={airline.logo_url} alt={airline.name ?? ''} loading="lazy" />
        <AvatarFallback>{fallback}</AvatarFallback>
      </Avatar>
    );
  }
  if (event.type === 'destination_arrival') {
    const countryCode = event.metadata.segment.destination.country_code;
    if (countryCode) return <div className="w-5">{getEmojiFlag(countryCode)}</div>;
  }
  if (isPlaceEvent(event)) {
    return <MaterialIcon icon={getPlaceIcon(event.metadata?.placePrimaryType)} size={IconSizes.sm} />;
  }
  return fallback;
}

type SharedVenueEvent = Exclude<SharedItineraryEvent, { metadata: { segment: unknown } }>;

function isPlaceEvent(event: SharedItineraryEvent): event is SharedVenueEvent {
  return event.type.startsWith('place_');
}

/** One line of the "Trip overview" day list; clicking scrolls to the event's full block. */
function OverviewLine({ event, onClick }: { event: SharedItineraryEvent; onClick: () => void }) {
  if (event.type === 'layover') {
    return <div className="@md:ml-[100px] pl-9 @md:pl-4 py-2 text-xs text-muted-foreground">{event.title}</div>;
  }

  const model = toModel(event);
  const time = event.type === 'hotel_checkin' ? '' : model.formattedTime;
  const title =
    event.type === 'hotel_checkin' && event.details?.name ? `Check in at ${event.details.name}` : event.title;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="flex flex-col @md:flex-row @md:items-center gap-1 @md:gap-4 hover:bg-secondary rounded-sm px-4 py-2.5 -mx-4 cursor-pointer"
    >
      <div className="text-muted-foreground w-[100px] flex-shrink-0 ml-9 @md:ml-0 text-sm @md:text-base">{time}</div>
      <div className="flex items-center gap-4">
        <OverviewIcon event={event} />
        <div>{title}</div>
      </div>
    </div>
  );
}

/**
 * The projection's destination sections with their days resolved. With no sections (a response from
 * a server that predates them) every day goes in one unlabelled section.
 */
function toOverviewSections(itinerary: SharedItineraryResponse) {
  const dayByKey = new Map(itinerary.days.map((day) => [day.dateKey, day]));
  const sections = itinerary.sections
    .map((section, index) => ({
      key: `${index}-${section.label}`,
      label: section.label as string | undefined,
      days: section.dateKeys.flatMap((key) => dayByKey.get(key) ?? []),
    }))
    .filter((section) => section.days.length > 0);
  return sections.length > 0 ? sections : [{ key: 'all', label: undefined, days: itinerary.days }];
}

/**
 * The public, read-only full-page itinerary. Same visual structure as the owner's itinerary page
 * (hero, summary, overview + map, day by day), built only from the allowlisted public projection,
 * with a quiet membership invitation at the top and a closing card at the end.
 */
export function SharedItineraryView({ itinerary }: { itinerary: SharedItineraryResponse }) {
  const sharedItineraryState = useSharedItineraryState();
  usePrimeImagesBeforePrint();
  const eventRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const mapPoints = React.useMemo(() => venueMapPoints(itinerary), [itinerary]);

  const scrollToEvent = React.useCallback((eventId: string) => {
    eventRefs.current.get(eventId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const { trip, days } = itinerary;
  const overviewSections = React.useMemo(() => toOverviewSections(itinerary), [itinerary]);
  const coverImage = trip.coverImageUrl ? resolveMediaUrl(trip.coverImageUrl) : undefined;

  const renderOverviewDay = (day: SharedItineraryResponse['days'][number]) => (
    <div key={day.dateKey} data-print="overview-day">
      <div className="flex items-center gap-2 border-b border-b-muted pb-2 mb-2.5 text-sm -mr-8">
        <div className="uppercase tracking-wide font-medium">Day {day.dayNumber}</div>
        <div className="text-muted-foreground">•</div>
        <div className="text-muted-foreground">{format(parseISO(day.dateKey), 'EEEE, MMMM do')}</div>
      </div>

      <div className="flex flex-col">
        {day.isTravelDay ? (
          <div className="text-sm text-muted-foreground">Traveling</div>
        ) : day.events.length === 0 ? (
          <div className="text-sm text-muted-foreground opacity-50">No activities scheduled.</div>
        ) : (
          day.events.map((event) => (
            <OverviewLine key={event.id} event={event} onClick={() => scrollToEvent(event.id)} />
          ))
        )}
      </div>
    </div>
  );

  return (
    // Framed like the elara.travel landing page: a rounded panel on camel from `md` up, full-bleed on
    // phones. `overflow-clip` (not `hidden`) rounds the corners without creating a scroll container,
    // so the sticky maps and photo panels keep sticking to the viewport.
    // `data-print-root` scopes the paper layout in `ui/itinerary/itinerary-print.css`.
    <div data-print-root="itinerary" className="min-h-dvh flex flex-col md:p-4 lg:p-6 md:bg-camel-100">
      <div
        data-print="frame"
        className="@container w-full flex-1 flex flex-col bg-background md:rounded-3xl md:overflow-clip mx-auto max-w-440"
      >
        <MembershipBanner />

        <ItineraryHero
          title={trip.title}
          coverImage={coverImage}
          subtitle={trip.startDate && trip.endDate ? formatDateRange(trip.startDate, trip.endDate) : undefined}
          cornerAction={
            // Tells visitors the page prints well (it has a paper layout); itself never printed.
            <span className="print:hidden">
              <Button
                variant={coverImage ? 'icon-glass' : 'icon'}
                size="icon"
                tooltip="Print itinerary"
                tooltipPosition="left"
                aria-label="Print itinerary"
                // Start fetching the lazy photos as the pointer arrives, so printing rarely has to wait.
                onPointerEnter={primeImagesForPrint}
                onClick={() => {
                  sharedItineraryState.recordPrinted();
                  void printPage();
                }}
              >
                <MaterialIcon icon="print" size="sm" />
              </Button>
            </span>
          }
        />

        {trip.summary && <ItinerarySummarySection summary={trip.summary} />}

        {days.length === 0 ? (
          <div className="text-muted-foreground text-center px-6 py-40 w-full border-b text-sm">
            Nothing has been added to this itinerary yet.
          </div>
        ) : (
          <>
            {/* Trip Overview */}
            <div data-print="overview" className="flex flex-col md:flex-row min-h-[60dvh] border-b">
              <div data-print="overview-list" className="flex-1 flex justify-end">
                <div
                  className={cn(
                    '@container flex flex-col gap-10 w-full max-w-[700px] py-8 md:py-16',
                    EDGE_PADDING_CLASSES,
                  )}
                >
                  {/* Split by destination, as the owner's itinerary builder does. */}
                  {/* Split by destination, as the owner's itinerary builder does. The header travels with
                      its first day (`overview-lead`) so a printed page never ends on a lone header. */}
                  {overviewSections.map((section) => {
                    const [firstDay, ...otherDays] = section.days;
                    return (
                      <div key={section.key} data-print="overview-section" className="flex flex-col gap-10">
                        <div data-print="overview-lead" className="flex flex-col gap-10">
                          {section.label && (
                            <OverviewDestinationHeader
                              label={section.label}
                              startDateKey={section.days[0]!.dateKey}
                              endDateKey={section.days.at(-1)!.dateKey}
                              dayCount={section.days.length}
                            />
                          )}
                          {firstDay && renderOverviewDay(firstDay)}
                        </div>
                        {otherDays.map(renderOverviewDay)}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div
                data-print="overview-map"
                className="sticky top-0 flex-1 flex min-h-[60dvh] max-h-[100dvh] bg-background-quaternary"
              >
                {mapPoints.length > 0 && (
                  <GoogleMap points={mapPoints} zoom={16} className="w-full h-full min-h-[60dvh]" />
                )}
              </div>
            </div>

            {/* Day-by-Day Itinerary */}
            {days.map((day) => (
              <div key={day.dateKey} data-print="day" className="flex flex-col items-center">
                <DayHeader number={day.dayNumber} date={parseISO(day.dateKey)} title={day.title} />

                {day.isTravelDay ? (
                  <div
                    data-print="day-note"
                    className="text-muted-foreground text-center px-6 py-12 w-full bg-background-secondary border-b text-sm"
                  >
                    Leaping across time zones today.
                  </div>
                ) : day.events.length === 0 ? (
                  <div
                    data-print="day-note"
                    className="text-muted-foreground text-center px-6 py-40 w-full border-b text-sm"
                  >
                    No activities scheduled.
                  </div>
                ) : (
                  day.events.map((event) => (
                    <div
                      key={event.id}
                      ref={(el) => {
                        if (el) eventRefs.current.set(event.id, el);
                      }}
                      data-print="event"
                      className="w-full"
                    >
                      <SharedEventBlock event={event} />
                    </div>
                  ))
                )}
              </div>
            ))}
          </>
        )}

        <MembershipCard />
      </div>
    </div>
  );
}
